import React from 'react';
import { Button, Checkbox, Dropdown, Form, Header, Icon, Input, Message, Segment } from 'semantic-ui-react';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';
import { EMAIL_PROVIDER_OPTIONS } from './importUtils';

const IntegrationCard = ({ title, tier, description, enabled, onToggle, children }) => (
    <Segment className={`Onboarding__recommendation ${enabled ? 'Onboarding__recommendation--selected' : ''}`}>
        <div className='Onboarding__recommendation-header'>
            {onToggle ? (
                <Checkbox toggle checked={enabled} onChange={(_, { checked }) => onToggle(checked)} label={<label><b>{title}</b></label>} />
            ) : <b>{title}</b>}
            {tier && <TierLabel tier={tier} />}
        </div>
        <p className='Onboarding__recommendation-summary'>{description}</p>
        {enabled && children && <div className='Onboarding__template-body'>{children}</div>}
    </Segment>
);

const Integrations = ({ integrations, errors, onChange, canContinue, onBack, onContinue }) => {
    const { email_provider, sync_calendar, web_form, lead_vendors, email_parsing, explanations = {} } = integrations;
    const vendorOptions = [...new Set([...lead_vendors.suggestions, ...lead_vendors.names])].map(name => ({ key: name, value: name, text: name }));
    const isGoogleOrOutlook = ['google', 'outlook'].includes(email_provider);

    return (
        <Segment>
            <Header as='h3'>
                Connect Your Tools
                <Header.Subheader>Choose where leads come from and which email and calendar your team uses.</Header.Subheader>
            </Header>
            <Form noValidate>
                <IntegrationCard
                    title='Email & Calendar'
                    tier={RECOMMENDATION_TIERS.REQUIRES_ACTIVATION}
                    description='Send emails from your own address and keep appointments in sync. Each user signs in to their own account after launch (My Profile → Integrations).'
                    enabled
                >
                    <Form.Group inline className='Onboarding__provider-options'>
                        {EMAIL_PROVIDER_OPTIONS.map(option => (
                            <Form.Radio
                                key={option.key}
                                label={option.text}
                                checked={email_provider === option.value}
                                onChange={() => onChange({ email_provider: option.value })}
                            />
                        ))}
                    </Form.Group>
                    {isGoogleOrOutlook && (
                        <Form.Checkbox
                            label='Also sync calendars'
                            checked={sync_calendar}
                            onChange={(_, { checked }) => onChange({ sync_calendar: checked })}
                        />
                    )}
                </IntegrationCard>

                <IntegrationCard
                    title='Website Contact Form'
                    tier={RECOMMENDATION_TIERS.RECOMMENDED}
                    description={explanations.web_form}
                    enabled={web_form.enabled}
                    onToggle={(enabled) => onChange({ web_form: { ...web_form, enabled } })}
                >
                    <Form.Field error={!!errors.web_form} width={10}>
                        <label>Form Name</label>
                        <Input value={web_form.name} onChange={(_, { value }) => onChange({ web_form: { ...web_form, name: value } })} />
                        {errors.web_form && <div className='Onboarding__field-error'>{errors.web_form}</div>}
                    </Form.Field>
                    <p className='Onboarding__hint'>After launch, customize and publish it from Settings → Integrations → Web Forms.</p>
                </IntegrationCard>

                <IntegrationCard
                    title='Lead Vendors'
                    tier={RECOMMENDATION_TIERS.NEEDS_CONFIRMATION}
                    description={`Do you buy leads from other companies? ${explanations.lead_vendors || ''}`}
                    enabled={lead_vendors.enabled}
                    onToggle={(enabled) => onChange({ lead_vendors: { ...lead_vendors, enabled } })}
                >
                    <Form.Field error={!!errors.lead_vendors}>
                        <label>Vendors</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            allowAdditions
                            additionLabel='Add vendor: '
                            placeholder='Pick or type vendor names'
                            options={vendorOptions}
                            value={lead_vendors.names}
                            onAddItem={() => {}}
                            onChange={(_, { value }) => onChange({ lead_vendors: { ...lead_vendors, names: value } })}
                        />
                        {errors.lead_vendors && <div className='Onboarding__field-error'>{errors.lead_vendors}</div>}
                    </Form.Field>
                    <p className='Onboarding__hint'>Each vendor is added as a data provider, so you can report on lead source and cost later.</p>
                </IntegrationCard>

                <IntegrationCard
                    title='Leads Sent by Email'
                    description='Some vendors and websites send leads as email notifications. DYL can read those emails and create leads automatically.'
                    enabled={email_parsing.enabled}
                    onToggle={(enabled) => onChange({ email_parsing: { ...email_parsing, enabled } })}
                >
                    <p className='Onboarding__hint'>After launch, set up an email parsing template in Settings → Integrations → Data Mapping.</p>
                </IntegrationCard>
            </Form>
            {!email_provider && <Message info size='small'>Pick your email provider, or choose "Something else, or not now".</Message>}
            <div className='Onboarding__footer'>
                <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
                <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                    Review Import & Integrations <Icon name='arrow right' />
                </Button>
                <div style={{ clear: 'both' }} />
            </div>
        </Segment>
    );
}

export default Integrations;
